import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import { ROUTES } from "@/lib/routes";
import { buildWhatsAppLink, SITE } from "@/lib/site";

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const DAY_STEPS = [
  {
    name: "Abhyangam",
    body:
      "A warm medicated oil massage, usually with oils like Dhanwantharam Thailam.",
  },
  {
    name: "Vethu Kuli",
    body: "A bath in water boiled with herbs such as Nalpamaradi leaves.",
  },
  {
    name: "Abdomen bandaging",
    body: "A cloth binding that supports the belly as it settles.",
  },
  {
    name: "Dhoopanam",
    body: "Herbal smoke to dry the hair and keep away cold.",
  },
  {
    name: "Pathya ahara",
    body:
      "Warm, simple food made for recovery and milk, with plenty of rest in between.",
  },
];

const CHOOSING_QS = [
  "Is a qualified Ayurveda doctor seeing you regularly, or only the staff?",
  "Will you have your own private space, or share a ward?",
  "Can your mother or husband stay with you?",
  "Is the food cooked fresh for new mothers?",
  "Where do the oils and medicines come from?",
];

export const BlogPostPage = () => {
  return (
    <PageLayout
      title={ROUTES.blog.title}
      description={ROUTES.blog.description}
      testId="blog-post-page"
    >
      <article className="pb-16 md:pb-24">
        {/* Hero */}
        <header className="pb-10 md:pb-16 bg-grain">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.div {...fade}>
              <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
                Guide · Kerala Postnatal Tradition
              </div>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-[1.08] tracking-tight">
                What is <span className="italic text-sage-700">Prasava Raksha</span>? A simple guide to Kerala's postnatal care.
              </h1>
              <p className="mt-6 text-base md:text-lg text-charcoal/70 leading-relaxed">
                In Kerala, a new mother was never left to recover alone. For 42
                days after delivery, the women of the house took over: warm oil
                every morning, a herbal bath, special food, and rest. This care
                is called Prasava Raksha, which simply means "protecting the
                mother after birth."
              </p>
            </motion.div>
          </div>
        </header>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-10 md:py-16 prose-like">
          <motion.section {...fade} className="mt-0">
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal tracking-tight">
              Why the first 42 days matter
            </h2>
            <p className="mt-5 text-base md:text-lg text-charcoal/75 leading-relaxed">
              Ayurveda calls this time Sutika Kala. The classical texts, like
              Ashtanga Hridayam, describe it as the time when the mother's body
              is most open and weak. Delivery leaves the body dry, tired and
              unsettled. In Ayurvedic terms, this is Vata out of balance.
            </p>
            <p className="mt-4 text-base md:text-lg text-charcoal/75 leading-relaxed">
              The aim of Prasava Raksha (or Sutika Paricharya) is to bring that
              balance back. Good care in these weeks helps the uterus return to
              its size, builds strength, supports milk, and protects the
              mother's mind as well as her body.
            </p>
          </motion.section>

          <motion.section {...fade} className="mt-14">
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal tracking-tight">
              What happens in a typical day
            </h2>
            <ol className="mt-6 space-y-5" data-testid="blog-day-steps">
              {DAY_STEPS.map((s, i) => (
                <li key={s.name} className="flex gap-4">
                  <span className="shrink-0 mt-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-sage/15 text-sage text-xs font-medium">
                    {i + 1}
                  </span>
                  <div>
                    <div className="font-serif text-lg md:text-xl text-charcoal">
                      {s.name}
                    </div>
                    <p className="mt-1 text-base text-charcoal/70 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.section>

          <motion.section {...fade} className="mt-14">
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal tracking-tight">
              Home, hospital or postnatal centre?
            </h2>
            <p className="mt-5 text-base md:text-lg text-charcoal/75 leading-relaxed">
              Today many families are small, and grandmothers live far away or
              work. That is why postnatal care centres have grown across Kerala.
              When you choose one, ask these questions:
            </p>
            <ul className="mt-6 space-y-3">
              {CHOOSING_QS.map((q) => (
                <li
                  key={q}
                  className="pl-5 border-l-2 border-sage/40 text-base text-charcoal/75 leading-relaxed"
                >
                  {q}
                </li>
              ))}
            </ul>
          </motion.section>

          <motion.section {...fade} className="mt-14 p-6 md:p-8 rounded-2xl bg-sand/60 border border-sage/15">
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal tracking-tight">
              Prasava Raksha at Kakkanat, Shoranur
            </h2>
            <p className="mt-5 text-base md:text-lg text-charcoal/75 leading-relaxed">
              At Kakkanat, we keep this tradition the way it was meant to be.
              Each mother has her own private villa, with a bystander staying
              alongside. Dr. Nimisha, our treating physician, sees you every 2
              days and plans your treatments. Our oils and medicines come from
              Kakkanat Ayurveda Asramam, our family's GMP-certified pharmacy.
            </p>
            <p className="mt-4 text-base md:text-lg text-charcoal/75 leading-relaxed">
              We have cared for mothers since 2015, and we host only three
              families at a time. Stays are 7, 14, 21, 28 or the full 42 days.
            </p>
            <p className="mt-4 text-base md:text-lg text-charcoal/75 leading-relaxed">
              Looking for Prasava Raksha near you? We are in Shoranur, about an
              hour from Thrissur and Palakkad.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/#pricing"
                className="inline-flex items-center gap-2 bg-charcoal text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-sage transition-colors"
                data-testid="blog-see-packages"
              >
                See our packages
                <ArrowRight size={15} />
              </a>
              <a
                href={buildWhatsAppLink("default")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-sage/40 text-charcoal px-6 py-3 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
                data-testid="blog-whatsapp"
              >
                WhatsApp {SITE.whatsappDisplay}
              </a>
            </div>
          </motion.section>

          <motion.p
            {...fade}
            className="mt-14 text-sm italic text-charcoal/55 font-serif"
          >
            Team Kakkanat Prasava Raksha
          </motion.p>
        </div>
      </article>
    </PageLayout>
  );
};

export default BlogPostPage;
